import { Emphases, LabelIds, RowOffsets } from "../consts";
import type {
  LabelRef,
  PriceAddOn,
  PriceDetailModel,
  PriceDetailRow,
} from "../types";

type BuildAddOnsBreakdownParams = {
  addOns: PriceAddOn[];
  addOnsTotal: number;
};

const UNIT_BASIS_LABEL_IDS = {
  per_hour: LabelIds.AddOnUnitPerHour,
  per_item: LabelIds.AddOnUnitPerItem,
  per_unit: LabelIds.AddOnUnitPerItem,
  per_person: LabelIds.AddOnUnitPerPerson,
} as const;

/**
 * Builds the "$X per hour" (or flat "$X") caption for an add-on line.
 * Matches OfferFlow `getCostString` wording.
 */
const addOnUnitPriceCaption = (
  unitCost: number | null | undefined,
  unitCostBasis?: string | null,
): LabelRef | undefined => {
  if (unitCost == null) return undefined;

  const unitLabelId =
    unitCostBasis != null
      ? UNIT_BASIS_LABEL_IDS[unitCostBasis as keyof typeof UNIT_BASIS_LABEL_IDS]
      : undefined;

  if (unitLabelId == null) {
    return {
      id: LabelIds.AddOnUnitPriceOnly,
      args: [{ money: unitCost }],
    };
  }

  return {
    id: LabelIds.AddOnUnitPrice,
    args: [{ money: unitCost }, { id: unitLabelId }],
  };
};

/**
 * Builds the presentation-neutral Add-ons total breakdown: one row per add-on
 * (with any secondary charges indented beneath it) followed by a strong total
 * row. Mirrors how `buildPriceSummary` produces rows for the main sections, so
 * the renderer stays a dumb binder with no pricing decisions.
 */
const buildAddOnsBreakdown = ({
  addOns,
  addOnsTotal,
}: BuildAddOnsBreakdownParams): PriceDetailModel => {
  const rows: PriceDetailRow[] = [];

  for (const addOn of addOns) {
    rows.push({
      key: addOn.id,
      label: { kind: "text", text: addOn.name },
      amount: addOn.amount,
      quantity: addOn.quantity,
      caption: addOnUnitPriceCaption(addOn.unitCost, addOn.unitCostBasis),
      emphasis: Emphases.Default,
      offset: RowOffsets.None,
      dividerBefore: false,
      itemDetails: addOn.itemDetails,
    });

    for (const charge of addOn.secondaryCharges ?? []) {
      rows.push({
        key: charge.id,
        label: { kind: "text", text: charge.name },
        amount: charge.amount,
        emphasis: Emphases.Default,
        offset: RowOffsets.Left,
        dividerBefore: false,
      });
    }
  }

  rows.push({
    key: "add-ons-total",
    label: { kind: "labelId", id: LabelIds.AddOnsTotal },
    amount: addOnsTotal,
    emphasis: Emphases.Strong,
    offset: RowOffsets.None,
    dividerBefore: true,
  });

  return { titleId: LabelIds.AddOnDetails, rows };
};

export type { BuildAddOnsBreakdownParams };
export { addOnUnitPriceCaption, buildAddOnsBreakdown };
